import React from "react";

import { differenceInDays } from "date-fns";

import useTranslations from "../../hooks/useTranslations";

import { MadType } from "../../screens/MadListScreen";
import {
  Badge,
  Link,
  Menu,
  MenuButton,
  MenuLink,
  MenuList,
} from "../wombat-ui";
import { AvatarGroup } from "../wombat-ui/Avatar";
import { BadgeVariant } from "../wombat-ui/components/Badge";
import { DotVerticalIcon } from "../icons";
import { MadFragment } from "../../graphql/fragments";
import UserAvatarGroup from "../utils/UserAvatarGroup";

type TimingBadgeProps = {
  startDate: string;
  endDate: string;
};

const TimingBadge: React.FC<TimingBadgeProps> = React.memo(
  ({ startDate, endDate }) => {
    const t = useTranslations();

    const today = new Date();
    const startDiff = differenceInDays(new Date(startDate), today);
    const startWithinSevenDays = startDiff > 0 && startDiff < 7;

    const endDiff = differenceInDays(new Date(endDate), today);
    const endsWithinSevenDays = endDiff < 7 && endDiff > 1;

    if (startWithinSevenDays)
      return <Badge variant="primary">{t("mads.startSoon")}</Badge>;
    if (endsWithinSevenDays && endDate)
      return <Badge variant="secondary">{t("mads.endSoon")}</Badge>;
    return null;
  }
);

function getBadgeColorByStatus(status: MadType): BadgeVariant {
  if (status === "next") return "secondary";
  if (status === "ongoing") return "primary";
  if (status === "finished") return "warning";
  if (status === "available") return "success";
}

type FollowUpCardProps = {
  mad: MadFragment;
  madType: MadType;
  isTalentViewing?: boolean;
};

const FollowUpCard: React.FC<FollowUpCardProps> = ({
  mad,
  madType,
  isTalentViewing = false,
}) => {
  const t = useTranslations();

  return (
    <li className="py-4 list-none" id={`followUpCard_${mad.id}`}>
      <div className="grid items-center justify-between grid-cols-5">
        <div className="flex items-center col-span-3">
          <Link
            to={`/mads/${mad.id}`}
            id={`${mad.id}-link`}
            className="text-sm font-medium truncate"
          >
            {mad?.template?.title && `${mad.template.title} - `}
            {mad.talent.name}
          </Link>
        </div>
        <div>
          <p className="pb-0.5 text-xs font-medium text-light-900">
            {t("mads.referent", { count: mad.referents.length })}
          </p>
          <UserAvatarGroup users={mad.referents} max={4} />
        </div>
        <div className="flex items-center justify-end">
          <Badge variant={getBadgeColorByStatus(madType)}>
            {t(`mads.status.${madType}` as const, { count: 1 })}
          </Badge>
          <TimingBadge startDate={mad.startDate} endDate={mad.endDate} />
          <div className="flex justify-end">
            {!isTalentViewing && (
              <Menu>
                <MenuButton icon={DotVerticalIcon} />
                <MenuList>
                  <MenuLink to={`/users/${mad.talent.id}`}>
                    {t("mads.seeTalentProfile")}
                  </MenuLink>
                </MenuList>
              </Menu>
            )}
          </div>
        </div>
      </div>
    </li>
  );
};

export default React.memo(FollowUpCard);
